// src/pages/notifications/NotificationsListPage.tsx
// Notification center — tabbed page with feed, channels, templates, etc.

import { useState, useMemo } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { notificationTabs, type NotificationTab } from '../../features/notifications/logic';
import { usePlatformRole } from '../../hooks/usePlatformRole';
import { NotificationFeed } from './NotificationFeed';
import { ChannelListPage } from './ChannelListPage';
import { TemplateListPage } from './TemplateListPage';
import { ScheduledListPage } from './ScheduledListPage';
import { ContactListPage } from './ContactListPage';
import { AnalyticsPage } from './AnalyticsPage';
import { UserPreferencesPage } from './UserPreferencesPage';

const TAB_COMPONENTS: Record<NotificationTab, React.FC> = {
  feed: NotificationFeed,
  channels: ChannelListPage,
  templates: TemplateListPage,
  scheduled: ScheduledListPage,
  contacts: ContactListPage,
  analytics: AnalyticsPage,
  preferences: UserPreferencesPage,
};

export const NotificationsListPage = observer(() => {
  const { t } = useI18n();
  const { isAdmin } = usePlatformRole();
  const [activeTab, setActiveTab] = useState<NotificationTab>('feed');

  const visibleTabs = useMemo(
    () => notificationTabs.filter(tab => !tab.adminOnly || isAdmin),
    [isAdmin],
  );

  // Reset to feed if current tab is no longer visible
  const safeTab = visibleTabs.some(tab => tab.id === activeTab) ? activeTab : 'feed';
  const ActiveComponent = TAB_COMPONENTS[safeTab];

  return (
    <div className="space-y-5">
      {/* The tab already names this page; the heading stays for screen readers. */}
      <h1 className="sr-only">{t('notifications.title')}</h1>

      {/* Tab navigation */}
      <nav className="flex gap-1 overflow-x-auto border-b border-border" aria-label={t('notifications.title')}>
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
              safeTab === tab.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      <ActiveComponent />
    </div>
  );
});

export default NotificationsListPage;
